import { mathematicalProgressText } from '../core/mathSession'
/**
 * What a run is doing, and what it did — one surface, because they are one record.
 *
 * D45 is the argument. `engine/run.ts` patches `moves` into `session.runs` after
 * every move, so a run's rows accumulate in the document WHILE IT WORKS. The feed
 * and the account are therefore the same view of the same rows, differing only in
 * whether `finishedAt` is set — there is no live projection here and no second
 * description of a run to drift out of step with the first.
 *
 * The moves used to be folded into a disclosure at the foot, which was right when
 * this surface could only be read after the fact. They are the body now: a run
 * that is working has nothing else to show, and a run that has finished is best
 * read as the sequence it actually was.
 *
 * D36's rule about not moving the reading under the reader is the THREAD's, and
 * `Thread.tsx` renders these surfaces as siblings with their own scroll containers
 * — so arrival here breaks nothing. But the reasoning applies again in its own
 * right, and `useStickToFoot` below is that rule for this column.
 *
 * Every number on this panel comes out of the stored `Run` record and NOTHING is
 * recomputed from the graph. That is not an optimisation: a node pruned or
 * re-checked after the fact must not be able to rewrite what the run says it did,
 * and a report assembled half from a record and half from the live session is a
 * report that changes when you tidy up.
 *
 * The shape of the page is the argument, and the feed moved it rather than
 * softened it. A run reads head, moves, foot: what you are looking at, what it
 * did, what it came to. The account is the FOOT because an account is an ending —
 * it opens with why it stopped, then what it spent, then, before anything that
 * could read as an achievement, how much it left open and how little of it has
 * been checked. Printed above the moves, as it was, that ending was stated over a
 * run that had not reached one.
 *
 * There is still no ranking of the run's output and no "most novel". The line
 * against a closing summary is drawn at its words now, not at the summary: the
 * run's message in the thread ends on what it found (`core/runFound.ts`) — held
 * up, didn't survive, still open, next — which is counts and names the record's
 * own checks and endings say, in the words this account prints them in, each a
 * press to where it is read in full, and nothing on it ranks, rates or praises.
 * A sentence claiming more than those counts would still be the tool declaring
 * it had found something, so this account keeps its own ending: the open-ends
 * figure rising against a spent budget.
 *
 * Runs read oldest first, which is the opposite of what this surface did before
 * the feed and is not a preference. Moves ascend inside a run — an argument that
 * went somewhere reads forwards — so runs have to ascend too, or the newest move
 * of the newest run lands in the middle of a column whose foot is being followed.
 * `runRowsFor`'s header has the whole of it. Nothing is lost by the flip, because the
 * account moved to the foot: the bottom of this column is the newest run's
 * ending, which is what a person opens this tab for.
 */

import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react'
import { JUDGE_ENDPOINT } from '../core/models'
import {
  MOVE_REASON,
  OP_LABEL,
  READING_LABEL,
  RUN_STOP_HEAD,
  checksSkippedLine,
  checksUnreadLine,
  emptyPassesFor,
  failedPassesFor,
  moveResult,
  modelPassesFor,
  panelRunTally,
  runProgress,
  untilLabel,
} from '../core/types'
import type { Run, RunStop } from '../core/types'
import { calendarDay } from '../core/dates'
import { PRICE_READ_ON, fmtUsd, priceOf } from '../core/models'
import { useStore } from '../core/store'
import { runRowsFor, statusOf, type RunRow } from '../core/runFeed'
import { runEndingsLine } from '../core/endings'
import { runReadings } from '../core/runFound'
import { Thoughtstream } from './Overlay'
import { RunAssessmentPanel, Summary } from './RunAssessmentPanel'

type FeedMove = Extract<RunRow, { kind: 'move' }>
type FeedFoot = Extract<RunRow, { kind: 'foot' }>

/** The reader's calendar day — see `core/dates.ts`. */
const fmtDay = calendarDay
const mins = (a: number, b?: number) => (b ? Math.max(1, Math.round((b - a) / 60000)) : null)

/** The line under each head. Written per ending, because each one is misread in
 *  its own way, and three of them are misread as being about the idea.
 *
 *  The failures are counted off the record rather than asserted: three different
 *  conditions produce the `unreachable` ending — four failures, a rejected key, or
 *  the key going away mid-run — and one of them involves no failure at all. A fixed
 *  "Four passes failed." would state a number the run never observed. */
function gloss(r: Run, stop: RunStop): string {
  switch (stop) {
    case 'moves':
      return `${r.moves.length} of ${r.budget.moves} logical moves spent.`
    case 'tokens':
      return `It stopped at move ${r.moves.length} rather than spend past what you were told it could.`
    case 'clock': {
      return r.budget.ms
        ? `${Math.round(r.budget.ms / 60000)} minutes from this run’s recorded start. A per-pass deadline separately bounds hung requests.`
        : 'The stored run names an elapsed limit but does not carry its duration.'
    }
    case 'protocol':
      return r.evidencePolicy
        ? `Profile “${r.evidencePolicy.profileName}” reached one of its recorded support, refutation, or decision-signal thresholds.`
        : 'A stored evidence-profile threshold was reached.'
    case 'size':
      // Two sentences, because two rules have written this ending. A run spends
      // `budget.nodes` on the nodes it adds (D74); before that the same field was
      // the size the GRAPH stopped at, and a record from then ended here with
      // `produced` well under it. Read off the record's own numbers rather than
      // the rule in force today, so neither run is told it did what it did not.
      return r.produced >= r.budget.nodes
        ? `It added ${r.produced} nodes, the most one run may, and stopped rather than keep adding.`
        : `The exploration reached ${r.budget.nodes} nodes, the size a run stopped at when this one ran, after it had added ${r.produced}.`
    case 'programme':
      return r.programme
        ? `Every one of the ${r.programme.steps.length} step${r.programme.steps.length === 1 ? '' : 's'} you approved was taken or skipped. The moves below are the list, in order.`
        : 'The approved list was walked to its end.'
    case 'pursuit':
      // Names the fact and not a verdict on the idea: a candidate stops when a
      // check refutes its synthesis, when the literature turns out to hold it
      // already, or when the synthesis was reasoned to a dead end. Which of
      // those it was is on the candidate's own card.
      return 'The candidate this run was confined to came off the ladder — refuted, already made, or closed. Its card says which.'
    case 'until':
      // The stored condition, in the same words the chip promised it in. A run that
      // ended `until` without one stored is a hand-edited document; the head line
      // alone still says what kind of ending it was.
      return r.budget.until
        ? `It found ${untilLabel(r.budget.until)} — the condition you gave it. The moves below are what that took.`
        : 'The condition you set was reached.'
    case 'spend':
      // Both figures, and the gap between them named rather than left to look like
      // an error. Someone who set $5 and reads "$4.12" has to be told the shortfall
      // was deliberate, or the ceiling looks broken in the direction that would
      // make them raise it.
      return `It stopped at move ${r.moves.length}, about ${r.estimatedUsd !== undefined ? fmtUsd(r.estimatedUsd) : 'part'} of ${r.budget.usd !== undefined ? fmtUsd(r.budget.usd) : 'the ceiling'} — one pass short of it, because a pass is checked before it runs rather than after. The shortfall is the guarantee.`
    case 'frontier':
      if (r.agenda)
        return 'No unattempted agenda step met the current dependency, revision and execution rules. This is a mechanical eligibility limit; mathematical possibilities remain open.'
      return 'Every open end is closed, folded away, past the depth a run works to, or has had every verb this policy spends. That is a fact about the list, not about the idea — select any node and press D, C, X, T, I or B to go past it.'
    case 'stalled': {
      // Its own count, off the record. This sentence used to say "a fact about
      // three passes" whatever the run had done, and was first read under a run
      // that had made one — three empty angles in a single response.
      const empty = emptyPassesFor(r.moves)
      const nothing = r.moves.filter((m) => m.outcome === 'nothing').length
      return `The model stopped finding distinct moves on the ends this run picked: ${nothing} logical move${nothing === 1 ? '' : 's'} came back with nothing new, across ${empty} model pass${empty === 1 ? '' : 'es'}. That is a fact about those passes, not about the idea.`
    }
    case 'declined':
      return 'Its own words are on each of those rows below. A refusal is a fact about the model, not about the claim.'
    case 'stopped':
      return `At move ${r.moves.length} of ${r.budget.moves}. The pass in flight was cancelled; what had already arrived was kept, and it was billed either way.`
    case 'unreachable': {
      // Counted the way the loop counted them, which includes a portfolio pass that
      // answered with nothing usable — see `failedPassesFor`. The last one's words
      // are its error row's, or else the note its broken angles carry.
      const failedOps = failedPassesFor(r.moves)
      const failed = failedOps.length
      const lastRows = r.moves.filter((m) => m.opId === failedOps.at(-1))
      const last = (lastRows.find((m) => m.outcome === 'error') ?? lastRows.find((m) => m.note))
        ?.note
      if (failed === 0) {
        return 'There was nothing left to call — the key was removed, or the model was changed to one whose provider has no key. Nothing failed; it simply stopped being able to ask.'
      }
      if (failed === 1 && last && /rejected/i.test(last)) {
        return `The key was rejected on the first pass, so it stopped there rather than repeat a request that could only fail again. ${last}`
      }
      return `${failed} pass${failed === 1 ? '' : 'es'} failed.${last ? ` The last said: ${last}` : ''}`
    }
    case 'replaced':
      return `A different exploration was opened, so the run stopped at move ${r.moves.length}.`
  }
}

/**
 * Follow the foot of the column, but only while the reader is already at it.
 *
 * D36's reasoning, applied to the surface where arrival is the point rather than
 * an intrusion. A feed that always scrolled would drag a reader who had gone up
 * to re-read move four; one that never scrolled would make "watch it work" mean
 * "keep scrolling down yourself". Sticking only when already stuck is the one
 * arrangement where both readings work, and it needs no state the reader has to
 * manage.
 *
 * IT WATCHES THE COLUMN'S SIZE RATHER THAN A LIST OF REASONS IT MIGHT CHANGE, and
 * that is the second shape this took. The first named its causes — a move count
 * and the length of the streaming thought — and review found two it did not name.
 * A run ENDING inserts the whole account foot without adding a move, so the
 * reader stranded above it was stranded at exactly the moment the surface exists
 * to carry them to. And `reason.ts` caps the trace at `thought.slice(-600)`, so
 * once a pass thinks past 600 characters the length stops changing while the text
 * keeps re-wrapping to a different number of lines. Both are the same failure:
 * an enumeration of causes is a list that can be short, and the thing actually
 * being tracked was never the causes but the height. A `ResizeObserver` on the
 * content tracks the height, so it also covers the causes nobody has thought of
 * yet — a window resize, a font landing late.
 *
 * The observer fires after layout and before paint, which is what the layout
 * effect it replaces was for: a passive effect would show one frame with the new
 * row in place and the column still where it was, and that reads as a jump rather
 * than as following.
 *
 * The threshold is generous on purpose. An exact-bottom test loses the reader the
 * moment a fractional scroll position or a sub-pixel row height puts them a
 * hair off, and the failure is silent — the feed simply stops following and
 * nothing says why.
 */
const FOOT_SLACK_PX = 48

function useStickToFoot() {
  const box = useRef<HTMLDivElement | null>(null)
  // Whether the reader was at the foot BEFORE this change landed, which is the
  // only moment the question can be asked: once the new rows are in the DOM,
  // `scrollHeight` has grown and a reader who never moved measures as though they
  // had scrolled away. So it is never measured at the moment of the change — the
  // scroll handler keeps it current, and what the observer reads is the answer
  // from the last time the reader actually moved. It starts true so opening the
  // tab lands on the newest run rather than on the oldest one still on the record.
  const stuck = useRef(true)

  // CALLBACK REFS RATHER THAN MOUNT EFFECTS, and the difference is not stylistic
  // — the effect version was a bug that the browser caught and every test passed
  // over. An effect with `[]` deps attaches on the render this component first
  // runs, and on a document with no runs that render returns the empty note:
  // there is no column yet to listen to. A run beginning while that tab is open
  // mounts the column on a LATER render, which `[]` never sees. So the listener
  // was never attached, `stuck` stayed true for the life of the panel, and a
  // reader who scrolled up to re-read move four was dragged back down by move
  // five — D36's complaint exactly, in the surface whose scroll rule exists to
  // prevent it. A callback ref follows the element instead of the mount.
  const scrollRef = useCallback((node: HTMLDivElement | null) => {
    box.current = node
    if (!node) return
    const onScroll = () => {
      stuck.current = node.scrollHeight - node.scrollTop - node.clientHeight <= FOOT_SLACK_PX
    }
    node.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      node.removeEventListener('scroll', onScroll)
      box.current = null
    }
  }, [])

  // The INNER element, because a scroll container's own box does not change when
  // its contents grow — `scrollHeight` does, and `ResizeObserver` does not report
  // that. So the rows live in a box of their own and it is that box which is
  // watched. `ResizeObserver` delivers a first observation on observe, which is
  // what lands the reader at the foot when the tab opens.
  const contentRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return
    const ro = new ResizeObserver(() => {
      const el = box.current
      if (!el || !stuck.current) return
      el.scrollTop = el.scrollHeight
    })
    ro.observe(node)
    return () => ro.disconnect()
  }, [])

  // What the "Read deeper" landing calls before it sends the reader somewhere
  // that is NOT the foot. `stuck` means "the reader is at the foot", and a
  // landing makes that false by definition, so following the foot afterwards is
  // wrong until they scroll back down themselves.
  //
  // It exists because the two used to settle their argument by accident. The
  // stick ran in a layout effect and the landing in a passive one, so the landing
  // simply went second and won — and D45 recorded that it rested on effect
  // ordering and would break silently if either were moved. Moving the stick to a
  // `ResizeObserver` moved it: an observer's first delivery lands AFTER passive
  // effects, so the stick started going second and yanked the reader off the card
  // they had just been sent to. Saying it outright costs one call and cannot come
  // undone by rearranging effects.
  const release = useCallback(() => {
    stuck.current = false
  }, [])

  return { scrollRef, contentRef, release }
}

/**
 * What a run was confined to and what list it walked — the two facts about a
 * run's shape the stop head does not carry. Read off the record and the
 * candidate list; a candidate dismissed since is named as such rather than
 * making the line vanish.
 */
function RunShape({ r }: { r: Run }) {
  const bridges = useStore((s) => s.session.bridges)
  if (!r.pursues && !r.programme) return null
  const pursued = r.pursues ? bridges.find((b) => b.id === r.pursues) : undefined
  const taken = r.programme ? r.moves.filter((m) => m.why === 'programme').length : 0
  const total = r.programme?.steps.length ?? 0
  return (
    <>
      {r.pursues && (
        <p className="gnote">
          A pursue run: confined to the two branches{' '}
          {pursued ? `“${pursued.title}”` : 'a candidate since dismissed'} fused and to the
          synthesis itself, which it checked against sources before anything else.
        </p>
      )}
      {r.programme && (
        <p className="gnote">
          A programme run: {taken} of the {total} step{total === 1 ? '' : 's'} you approved{' '}
          {taken === 1 ? 'was' : 'were'} taken, in the order {r.programme.model.name} proposed them
          {r.stop === 'programme' && taken < total
            ? '; the rest were skipped — their claim was gone or was the seed by the time they were reached, the budget did not cover that kind of pass, or the pass could not be started'
            : ''}
          .
        </p>
      )}
    </>
  )
}

export function RunReport() {
  const mathematicalSession = useStore((s) => s.session)
  const runs = useStore((s) => s.session.runs)
  // Which run THIS tab is running, or null. The record cannot say — an
  // unfinished run and an abandoned one are the same bytes — so `statusOf`
  // takes it. Read as an id rather than the whole projection so a run's own
  // moves landing do not re-render this on every tick of the pod's counts.
  const liveRunId = useStore((s) => (s.run && s.run.phase !== 'ended' ? s.run.id : null))
  /**
   * Whether the line at the foot is THE RUN'S to show.
   *
   * `busy`, `activeOp` and `thought` are one global channel — `beginOp` is the
   * only place `busy` is set, and it sets all three — so a run being live is not
   * enough to say whose thinking is on it. The run deliberately RELEASES the pass
   * lock between moves (`waitForIdle` waits on `busy`; `RunPod`'s header explains
   * why), which makes a manual verb during the gap a supported thing to do rather
   * than a race. In that window the run is still `running` and the stream carries
   * the manual pass, so an ungated line printed someone's own hand-fired pass
   * inside the autonomous run's card, as a row that would never land — no
   * `RunMove` is ever recorded for it.
   *
   * Two cases, not one. `run.now` non-null is the run's own pass in flight. And
   * `!busy` is the gap with nothing in it, where the retained last thought is
   * still the run's — kept because `RunPod` makes the same point about its own
   * chip: a panel that empties for four seconds every minute reads as a stall
   * rather than as a gap. What is excluded is exactly the third case, where
   * something else holds the lock.
   */
  const runOwnsTheLine = useStore((s) =>
    s.run !== null && s.run.phase !== 'ended'
      ? // At a confirm gate `now` is the PROPOSED move and stays non-null for
        // the whole wait, so there it cannot stand in for "the run's own pass
        // is in flight" — the lock alone separates the run's held gap from a
        // pass somebody hand-fired into it.
        s.run.phase === 'waiting'
        ? !s.busy
        : s.run.now !== null || !s.busy
      : false,
  )
  const { scrollRef, contentRef, release } = useStickToFoot()
  // A node's "Read deeper" line can send the reader to that node's OWN producing
  // run, not to whatever ran last. The report still lists every run; this scrolls
  // the named one into view and lights it, so the eye lands on it among the rest.
  const runFocusId = useStore((s) => s.runFocusId)
  const clearRunFocus = useStore((s) => s.clearRunFocus)
  const cardRefs = useRef(new Map<string, HTMLDivElement>())
  const [flashId, setFlashId] = useState<string | null>(null)

  // Land on the run a node's "Read deeper" link named. `focusRun` sets
  // `runFocusId` and switches to this tab, so this runs as RunReport mounts:
  // scroll that run's card into view, light it, and consume the request so a
  // later reopen of the tab never re-fires on a stale id.
  useEffect(() => {
    if (!runFocusId) return
    // Only when the reader is being sent AWAY from the foot — which the run at
    // the foot, by definition, is not. Releasing unconditionally was an
    // over-correction: `scrollIntoView({ block: 'nearest' })` does nothing at all
    // when the target is already in view, so it emits no scroll event, so nothing
    // ever restored `stuck`. Someone who pressed a live run's "as it happens" on
    // a card that already fitted the viewport landed at the live foot with
    // following switched off, and then watched the moves they had come for slide
    // out from under them.
    //
    // The last run IS the foot — the column is chronological — so targeting it
    // leaves the stick alone. If that card is taller than the viewport the reader
    // lands on its head and the next arrival carries them down, which is not a
    // misbehaviour: the press they used says "as it happens".
    //
    // Before the scroll rather than after, still: the column must stop following
    // from this moment rather than from whenever the smooth scroll happens to
    // emit its first event.
    if (runFocusId !== runs.at(-1)?.id) release()
    const el = cardRefs.current.get(runFocusId)
    // A run scrolled offscreen, or one pruned from the record since, simply has no
    // card to land on — the tab still opened, which is the load-bearing half.
    if (el && typeof el.scrollIntoView === 'function') {
      // The flash above obeys the global reduced-motion rule (it is a CSS
      // animation); a programmatic smooth scroll does not, so honour the query by
      // hand — an instant jump for anyone who asked motion to stop.
      const still =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      el.scrollIntoView({ block: 'nearest', behavior: still ? 'auto' : 'smooth' })
    }
    setFlashId(runFocusId)
    clearRunFocus()
  }, [runFocusId, clearRunFocus, release, runs])

  // The light fades on its own. Kept off the effect above so consuming
  // `runFocusId` there cannot cancel this timer, and so re-lighting the same card
  // later toggles the attribute off first and re-runs the animation.
  useEffect(() => {
    if (!flashId) return
    const t = setTimeout(() => setFlashId(null), 1600)
    return () => clearTimeout(t)
  }, [flashId])

  if (!runs.length) {
    return (
      // The press is named where it is, in the strip's own word: this note went
      // on saying "the composer's tune panel" after the run moved to the row
      // above the input (D179, D338).
      <div className="empty-note">
        Nothing has run on its own in this exploration yet. Run, on the row above the input, spends
        a budget of moves on the graph’s open ends and then tells you what it did.
      </div>
    )
  }

  // The order, and which runs have come to anything, are BOTH `runRowsFor`'s — this
  // component asks for rows and gets them oldest first, because the direction has
  // to be the same at both levels for arrival to be at the foot. Grouping the flat
  // list back into cards is a fold over consecutive rows, not a second ordering:
  // `runRowsFor` emits a run's rows together and `runId` is on every one of them so a
  // renderer can group without the structure having to.
  const cards: { run: Run; rows: FeedMove[]; foot: FeedFoot | null }[] = []
  for (const item of runRowsFor(runs, liveRunId)) {
    if (item.kind === 'head') {
      cards.push({ run: item.run, rows: [], foot: null })
      continue
    }
    const card = cards.at(-1)
    // A row before its head is not something `runRowsFor` can emit — a head is
    // pushed for every run before any of its rows. Dropped rather than asserted
    // away, so a future change to that order shows up as a missing row and not as
    // a crash in a panel someone opened to find out what went wrong.
    if (!card) continue
    if (item.kind === 'move') card.rows.push(item)
    else card.foot = item
  }

  return (
    // Two boxes rather than one, and the split is what makes the column
    // followable: `.scroll` is what scrolls and `.insp` is what grows, and a
    // `ResizeObserver` can only report a box that changes. `.insp` carries the
    // padding and the gap exactly as it did when the two were one element.
    <div className="scroll runreport" ref={scrollRef}>
      <div className="insp" ref={contentRef}>
        {mathematicalSession.research?.mathematics ? (
          <section aria-label="Current mathematical obligations">
            <h3>Current mathematical obligations</h3>
            {mathematicalProgressText(mathematicalSession).map((line, i) => (
              <p key={i}>{line}</p>
            ))}
            <button
              type="button"
              onClick={() => useStore.getState().setResearchOpen(true, 'mathematics')}
            >
              Inspect proof dependencies
            </button>
          </section>
        ) : null}
        {cards.map(({ run: r, rows, foot }) => {
          const status = statusOf(r, liveRunId)
          const ran = mins(r.startedAt, r.finishedAt)
          // An insight pass that landed none is an answer, not a branch with nothing
          // left in it, and is counted with the insights below instead.
          const nothing = r.moves.filter(
            (m) => m.outcome === 'nothing' && m.kind !== 'conjecture',
          ).length
          const conjecturePasses = r.moves.filter((m) => m.kind === 'conjecture')
          const conjectured = conjecturePasses.reduce((a, m) => a + m.produced, 0)
          const searches = r.moves.filter((m) => m.kind === 'priorArt').length
          const refusedAngles = r.moves.filter((m) => m.outcome === 'refused').length
          const refused = new Set(r.moves.filter((m) => m.outcome === 'refused').map((m) => m.opId))
            .size
          const failedAngles = r.moves.filter((m) => m.outcome === 'error').length
          const failed = new Set(r.moves.filter((m) => m.outcome === 'error').map((m) => m.opId))
            .size
          const incomplete = r.moves.filter((m) => m.outcome === 'incomplete').length
          const passes = modelPassesFor(r.moves)
          const checkMoves = r.moves.filter((m) => m.kind === 'verify').length
          const panel = panelRunTally(r.moves)
          const panelMoves = panel.moves
          const auditMoves = r.moves.filter((m) => m.kind === 'audit').length
          // From the stored tally, never counted off the graph. An objection lands as an
          // ordinary contradiction node, so a user who has since pruned one would
          // otherwise be editing the survival count of a pass that already happened.
          const examined = r.auditVerdicts?.filter((v) => v !== 'insufficient-context').length ?? 0
          const missingContext =
            r.auditVerdicts?.filter((v) => v === 'insufficient-context').length ?? 0
          const broke = r.auditVerdicts?.filter((v) => v === 'objection').length ?? 0
          // The auditor, off the moves rather than off `r.model` — which names the model
          // whose work was being ATTACKED. Read from the op's own stamp is not available
          // here, so the name is deliberately omitted rather than guessed at: saying
          // "survived X" where X is the model that wrote the claims would invert the one
          // provenance claim this pass rests on.
          const openTotal = r.open.reachable + r.open.pastDepth + r.open.folded
          return (
            <div
              className="group runacct"
              key={r.id}
              ref={(el) => {
                if (el) cardRefs.current.set(r.id, el)
                else cardRefs.current.delete(r.id)
              }}
              data-flash={flashId === r.id || undefined}
              style={{ gap: 'var(--s2)' }}
            >
              <span className="eyebrow">
                Run · {fmtDay(r.startedAt)}
                {ran ? ` · ${ran} min` : ''} · {r.model.name}
              </span>

              {/* The head says what you are looking at, and nothing about how it
                turned out — that is the foot's, and a run still working has not
                turned out yet. Before the feed there was no ordinary way to read
                this surface mid-run, so the account spoke for both cases and told
                a reader watching their run work that its page had closed. */}
              {status === 'running' && (
                <p className="gnote">
                  This run is going now. What it has done so far is below, and the rest arrives here
                  as it happens.
                </p>
              )}

              {r.evidencePolicy ? (
                <details>
                  {/* The pane's fold, as the review panel below wears it. */}
                  <Summary>
                    Recorded protocol · {r.evidencePolicy.profileName} v
                    {r.evidencePolicy.profileVersion}
                  </Summary>
                  {r.evidencePolicy.profile ? (
                    <>
                      <p className="gnote">
                        {r.evidencePolicy.profile.search.policy === 'recorded-search-v1'
                          ? 'Recorded search work. Negative results describe the recorded scope and do not establish absence or novelty.'
                          : 'Historical coverage policy. Its recorded percentage is an uncalibrated calculation, retained under its original rules.'}
                      </p>
                      <pre>{JSON.stringify(r.evidencePolicy.profile, null, 2)}</pre>
                    </>
                  ) : (
                    <p className="gnote">
                      This older run retained its profile identity and budget/stopping clauses, but
                      no complete profile snapshot. Its missing search rules are unknown; the
                      current profile is not substituted.
                    </p>
                  )}
                </details>
              ) : null}

              {/* THE BODY, not a disclosure. These rows were folded away when this
                surface could only be read after the fact; a run that is still
                working has nothing else to show, and one that has finished reads
                best as the sequence it actually was. Each row keeps its
                MOVE_REASON line — the rule that chose it — because a move
                without its reason is a claim that the run went somewhere for no
                stated cause. */}
              {rows.length > 0 && (
                <div className="group" style={{ gap: 'var(--s2)' }}>
                  <span className="eyebrow">
                    {status === 'running' ? 'What it is doing' : 'Every move, in order'}
                  </span>
                  <ol className="feedmoves">
                    {rows.map(({ move: m, ordinal }) => (
                      <li key={`${m.opId}:${ordinal}`}>
                        <b className="num">{ordinal}</b> {OP_LABEL[m.kind]}
                        {m.label ? ` · ${m.label}` : ''} — {moveResult(m)}
                        <br />
                        <span className="srcs-meta">
                          {MOVE_REASON[m.why]}
                          {m.note ? ` — “${m.note}”` : ''}
                        </span>
                        {/* The judge's number on a check, printed whether or not it
                            decided the tie: the run paid for it, so the reader sees it. */}
                        {m.judge && (
                          <>
                            <br />
                            <span className="srcs-meta">
                              {m.judge.model.name} put the probability that a search could settle
                              this claim at {m.judge.checkability.toFixed(2)}.
                            </span>
                          </>
                        )}
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* THE FOOT — what the run came to, printed where it happened. It
                is `runRowsFor` that decides there is one, and it withholds it from
                exactly one run: the one going now, whose counts are still moving
                and whose live figures are `RunPod`'s. Every account here is a
                final tally read off the record, so printing one over a run in
                flight would be stating an ending it has not reached. */}
              {foot && (
                <div className="group feedfoot" style={{ gap: 'var(--s2)' }}>
                  {foot.stop ? (
                    <>
                      <p className="runpod-head">{RUN_STOP_HEAD[foot.stop]}</p>
                      <p className="gnote">{gloss(r, foot.stop)}</p>
                      <RunShape r={r} />
                    </>
                  ) : (
                    // No ending recorded and nothing running it: the tab was closed
                    // while it was going. The absence is the information, exactly as
                    // it is for an unfinished op — and the counts below it are the
                    // last thing the run wrote down rather than a final tally, which
                    // is why they read under this sentence and not over it.
                    <p className="gnote gnote--warn">
                      This run has no ending recorded — the page closed while it was going. What it
                      had done by then is above.
                    </p>
                  )}
                  <p className="gnote">{runProgress(r.moves.length, r.budget.moves, passes)}</p>

                  {/* Counts, numerators only, and a row at zero is not printed: a nought
                reads as a target missed rather than as a thing that did not happen. */}
                  <ul className="ground">
                    {r.produced > 0 && (
                      <li style={{ '--g-tint': 'var(--sem-signal)' } as CSSProperties}>
                        <b className="num">{r.produced}</b> node{r.produced === 1 ? '' : 's'}{' '}
                        threaded
                      </li>
                    )}
                    {/* In the endings' words (`runEndingsLine`): the count takes in
                        questions reasoning cannot settle, and "dead end" called
                        those what they are not. */}
                    {r.dead > 0 && (
                      <li style={{ '--g-tint': 'var(--sem-dead)' } as CSSProperties}>
                        {runEndingsLine(r)}
                      </li>
                    )}
                    {nothing > 0 && (
                      <li style={{ '--g-tint': 'var(--sem-weak)' } as CSSProperties}>
                        <b className="num">{nothing}</b> move{nothing === 1 ? '' : 's'} returned
                        nothing new
                      </li>
                    )}
                    {refusedAngles > 0 && (
                      <li style={{ '--g-tint': 'var(--sem-gap)' } as CSSProperties}>
                        <b className="num">{refusedAngles}</b> logical angle
                        {refusedAngles === 1 ? '' : 's'} declined across {refused} model pass
                        {refused === 1 ? '' : 'es'}
                      </li>
                    )}
                    {failedAngles > 0 && (
                      <li style={{ '--g-tint': 'var(--sem-tension)' } as CSSProperties}>
                        <b className="num">{failedAngles}</b> logical angle
                        {failedAngles === 1 ? '' : 's'} failed across {failed} model pass
                        {failed === 1 ? '' : 'es'}
                      </li>
                    )}
                    {incomplete > 0 && (
                      <li style={{ '--g-tint': 'var(--sem-gap)' } as CSSProperties}>
                        <b className="num">{incomplete}</b> portfolio angle
                        {incomplete === 1 ? '' : 's'} ended before a complete result arrived
                      </li>
                    )}
                    {r.declined > 0 && (
                      <li style={{ '--g-tint': 'var(--accent)' } as CSSProperties}>
                        <b className="num">{r.declined}</b> branch{r.declined === 1 ? '' : 'es'} you
                        closed while it ran
                      </li>
                    )}
                  </ul>

                  {/* What it spent, in the one place on this panel where money belongs:
                after the counts and before the open ends, which is the order the
                header describes. Printed for every run and not only for one under a
                ceiling — a figure someone can only see if they thought to ask for a
                limit is a figure that arrives too late to be useful.

                "About" and "estimated" in the same sentence as the number, every
                time. This is an upper figure from a list-price table with a date on
                it, and a surface that shortened it to a bare dollar amount would be
                claiming a precision the arithmetic in `estimateUsd` deliberately
                does not have. */}
                  {r.estimatedUsd !== undefined && (
                    <p className="gnote">
                      About {fmtUsd(r.estimatedUsd)}
                      {r.budget.usd !== undefined
                        ? ` of your ${fmtUsd(r.budget.usd)} ceiling`
                        : ''}{' '}
                      — estimated at list prices read on {PRICE_READ_ON}, each token at the rate its
                      kind bills at: a cache read at the read rate, a cache write with its premium,
                      a page opened for nothing where the provider charges nothing for one, and
                      anything a receipt does not break down at the full rate. Discounts and credits
                      are not counted, so the real charge can be lower. Not a bill.
                    </p>
                  )}
                  {/* The two ways the figure can be missing are not the same, and only one
                of them is worth a sentence. A model this app has no rates for gets
                one, because other runs in this same list print a dollar figure and
                its absence here would otherwise read as a bug. A run recorded before
                this app estimated anything gets silence: the number was never taken,
                and inventing an explanation for a gap in an old record is worse than
                the gap. */}
                  {/* Beside the figure, because it is the ceiling's doing: checks the
                      schedule reached with too little left to hold one back. The
                      threshold is the record's own — `checksSkipped` stores the
                      reserve the run was held to, not today's price. */}
                  {r.checksSkipped && <p className="gnote">{checksSkippedLine(r.checksSkipped)}</p>}
                  {/* And the route's doing, beside the ceiling's: checks that had
                      nothing to read, and the model that had no search (D293). */}
                  {r.checksUnread && <p className="gnote">{checksUnreadLine(r.checksUnread)}</p>}
                  {r.estimatedUsd === undefined && priceOf(r.model.id) === null && (
                    <p className="gnote gnote--quiet">
                      No spend estimate for this one. {r.model.name} ran on an endpoint you
                      supplied, and what it charges is between you and whoever runs it — the ledger
                      below has the tokens.
                    </p>
                  )}

                  {/* The open ends, broken out. One number would hide exactly where it
                matters — a run that hit the depth ceiling left a whole ring
                untouched, and folding that into "reachable" would turn the figure
                built to prevent an impression of completeness into the figure that
                creates it. */}
                  <p className="gnote">
                    {openTotal} open end{openTotal === 1 ? '' : 's'} left: {r.open.reachable} this
                    policy could still have taken, {r.open.pastDepth} past the depth a run works to,{' '}
                    {r.open.folded} inside branches you folded away.
                  </p>

                  {/* Printed whatever the count. Rendering this only when something was
                checked would delete the sentence at exactly the moment it carries
                the most weight — a run whose entire output rests on the model and
                says so nowhere. */}
                  {r.checkReadings.length > 0 ? (
                    <p className="gnote">
                      {r.checkReadings.length} claim{r.checkReadings.length === 1 ? '' : 's'} sent
                      to a source check —{' '}
                      {runReadings(r, mathematicalSession)
                        .map((x) => READING_LABEL[x].toLowerCase())
                        .join(', ')}
                      . Every other node it added rests on the model alone.
                    </p>
                  ) : (
                    <p className="gnote gnote--warn">
                      {checkMoves > 0
                        ? 'No check recorded a reading, so nothing this run added has been put in front of a source.'
                        : 'No claim this run added was put in front of a source. All of it rests on the model alone.'}
                    </p>
                  )}

                  {/* How the checks were ORDERED, said either way. A judge that
                      was consulted is named with what it did and did not do; a run
                      with none says so, because a reader comparing two accounts
                      is owed the difference in the rule and not only in the
                      result. */}
                  {r.judge ? (
                    <p className="gnote">
                      {r.judge.model.name} rated {r.judge.asked} candidate claim
                      {r.judge.asked === 1 ? '' : 's'} on whether a search could settle them, before
                      the checks. It ordered ties only — what rests on a claim and how open it is
                      still came first — and it removed nothing.
                      {r.judge.failed > 0
                        ? ` ${r.judge.failed} of its call${r.judge.failed === 1 ? '' : 's'} did not answer${r.judge.note ? ` — ${r.judge.note}` : ''}; the claims in ${r.judge.failed === 1 ? 'it' : 'them'} were ordered by arrival.`
                        : ''}
                    </p>
                  ) : (
                    checkMoves > 0 && (
                      <p className="gnote">
                        {/* On a build with no judge endpoint there was nothing to
                            configure, so the sentence must not suggest there was: a
                            reader of the public build has no key field to go looking
                            for (D107). Only a build that names one is told the judge
                            was left unconfigured. */}
                        {JUDGE_ENDPOINT
                          ? 'No judge was configured, so ties among the claims a check could take went to arrival order.'
                          : 'Ties among the claims a check could take went to arrival order.'}
                      </p>
                    )
                  )}

                  {/* The panel, when one ran. A split is the finding worth reading — three
                lenses that did not agree marks an unsettled question — but this is a
                count of passes, not a reading of them, so it says what it spent and
                leaves the verdicts on the nodes that carry them. */}
                  {panelMoves > 0 && (
                    <p className="gnote">
                      {panelMoves} claim{panelMoves === 1 ? '' : 's'} sent to a three-lens panel;{' '}
                      {panel.passes} model request{panel.passes === 1 ? '' : 's'} began.
                      {panel.partial > 0
                        ? ` ${panel.partial} panel${panel.partial === 1 ? '' : 's'} stopped before ${panel.partial === 1 ? 'its' : 'their'} third request.`
                        : ''}{' '}
                      Any disagreement that returned is recorded on its node.
                    </p>
                  )}

                  {/* The cross-examination, and the one number in this account that came from
                outside the house that wrote the graph. Stated as survival and not as
                soundness: a claim survived because a model told to break it could not,
                which is a fact about that attempt and not a verdict on the claim. */}
                  {auditMoves > 0 &&
                    (examined > 0 ? (
                      <p className={broke > 0 ? 'gnote gnote--warn' : 'gnote'}>
                        {examined - broke} of {examined} completed reviews found no specific
                        objection
                        {broke > 0
                          ? `. ${broke} proposed criticisms need their arguments checked.`
                          : '. That is a fact about one attempt to break them, not a verdict on whether they are right.'}
                        {missingContext > 0
                          ? ` ${missingContext} reviews need more context and are excluded from that count.`
                          : ''}
                      </p>
                    ) : (
                      <p className="gnote gnote--warn">
                        {auditMoves} cross-examination{auditMoves === 1 ? '' : 's'} ran and returned
                        no usable verdict, so nothing here has been attacked by another
                        company&rsquo;s model.
                      </p>
                    ))}

                  {/* Insights, when any pass proposed them: counts of what was
                proposed and searched, never a verdict on any of them. Whether one
                has been said before is its search's to report, on its card. The
                pointer names the fold by its title, as the end card's lines do: it
                said "the conjecture ledger", which no heading on screen is called,
                and since D333 the fold and these counts say "insight", the reader's
                word for a conjecture. */}
                  {conjecturePasses.length > 0 && (
                    <p className="gnote">
                      {conjecturePasses.length} insight pass
                      {conjecturePasses.length === 1 ? '' : 'es'} proposed {conjectured} insight
                      {conjectured === 1 ? '' : 's'}
                      {searches > 0
                        ? `, and ${searches} prior-art search${searches === 1 ? '' : 'es'} took ${searches === 1 ? 'a check slot' : 'check slots'}`
                        : ''}
                      . Each is a proposal with a prediction; “Insights” in the Overview shows what
                      each has been through.
                    </p>
                  )}

                  {r.scanned !== undefined && (
                    <p className="gnote">
                      The scan proposed {r.scanned} candidate pair{r.scanned === 1 ? '' : 's'}.
                      {r.scanned > 0 &&
                        ' None realised — whether a pair actually fuses is the one judgement here worth a person.'}
                    </p>
                  )}
                </div>
              )}

              {/* THE ROW THAT HAS NOT LANDED YET, where the next one will. Phase
                four called this the feed's head, which was written when runs
                listed newest first; the direction flip moved arrival to the foot
                and took the live line with it. That is the better place on its
                own terms — what the model is thinking now comes AFTER the last
                move it recorded, and when the pass finishes its row appears
                under this line and pushes it down again.

                It also renders over the canvas throughout, and that is not a
                duplicate by accident: a reader watching the graph rather than
                this column would otherwise lose the only line that says what the
                model is thinking. `inFeed` is what keeps the two copies from
                being announced twice. */}
              {status === 'running' && runOwnsTheLine && <Thoughtstream inFeed />}

              {r.finishedAt !== undefined && <RunAssessmentPanel runId={r.id} />}

              {/* Undoing a run is something you do after one. Printed over a run
                still working, it offers to put back a graph that is still being
                written. */}
              {status !== 'running' && (
                <p className="gnote gnote--quiet">
                  ⌘Z puts the graph back to before the run — though a step you took while it was
                  running comes back first. Its estimated spend stays on the ledger.
                </p>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
